import { Box, SxProps, Theme, useTheme } from "@mui/material";
import BookingTypeSelector from "../../../components/Reports/CapacityFilter/DateAndTypeSelector/booking-type-selector.partial";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { dashboardToggleButtonGroupSx } from "../../Dashboard/dashboard-toggle-button-group.styles";
import type { ResourceKind } from "../typings/resource-calendar.types";
import {
  isStandaloneResourceBookingActive,
  resourcePlaceBookingTypeToggleValue
} from "../utils/resource-booking-schedule.utils";
import { ScheduleResourceKindToggle } from "./ScheduleResourceKindToggle.partial";

export type ScheduleResourceBookingTypeControlsProps = Readonly<{
  placeBookingTypes: BookingType[];
  resourceBookingType: BookingType | null;
  resourceKind: ResourceKind;
  showResourceKindToggle: boolean;
  onResourceBookingTypeChange: (type: BookingType) => void;
  onResourceKindChange?: (kind: ResourceKind) => void;
  onResourceFetchEnable?: () => void;
  buttonStyle?: SxProps<Theme>;
  iconSize?: string;
  compact?: boolean;
}>;

export const scheduleResourceBookingTypeControlsSx = (
  theme: Theme,
  options?: { compact?: boolean; hasDivider?: boolean }
) => ({
  ...dashboardToggleButtonGroupSx(theme, { compactTouchTargets: options?.compact }),
  display: "inline-flex",
  alignItems: "stretch",
  "& .MuiToggleButtonGroup-root": {
    border: "none",
    borderRadius: 0,
    margin: 0
  },
  ...(options?.hasDivider
    ? {
        "& .MuiToggleButtonGroup-root + .MuiToggleButtonGroup-root": {
          borderLeft: 1,
          borderColor: "divider"
        }
      }
    : {}),
  ...(options?.compact
    ? {}
    : {
        "& .MuiToggleButton-root": {
          minWidth: 48,
          minHeight: 48
        }
      })
});

export function ScheduleResourceBookingTypeControls({
  placeBookingTypes,
  resourceBookingType,
  resourceKind,
  showResourceKindToggle,
  onResourceBookingTypeChange,
  onResourceKindChange,
  onResourceFetchEnable,
  buttonStyle,
  iconSize,
  compact = false
}: ScheduleResourceBookingTypeControlsProps) {
  const theme = useTheme();
  const hasPlaceTypes = placeBookingTypes.length > 0;

  if (!hasPlaceTypes && !showResourceKindToggle) {
    return null;
  }

  return (
    <Box
      data-testid="schedule-resource-booking-type-controls"
      sx={scheduleResourceBookingTypeControlsSx(theme, {
        compact,
        hasDivider: hasPlaceTypes && showResourceKindToggle
      })}
    >
      {hasPlaceTypes ? (
        <BookingTypeSelector
          buttonStyle={buttonStyle}
          iconSize={iconSize}
          withTrailingMargin={false}
          typToggle={resourcePlaceBookingTypeToggleValue(resourceBookingType)}
          setTypToggle={nextType => {
            onResourceBookingTypeChange(nextType);
          }}
          onEnableFetch={() => {
            onResourceFetchEnable?.();
          }}
          allowedBookingTypes={placeBookingTypes}
        />
      ) : null}
      {showResourceKindToggle ? (
        <ScheduleResourceKindToggle
          buttonStyle={buttonStyle}
          iconSize={iconSize}
          selectedKind={
            isStandaloneResourceBookingActive(resourceBookingType) ? resourceKind : null
          }
          onResourceKindChange={kind => {
            onResourceBookingTypeChange(BookingType.RESOURCE);
            onResourceKindChange?.(kind);
            onResourceFetchEnable?.();
          }}
        />
      ) : null}
    </Box>
  );
}
